import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sessionExpired } from "../features/auth/user.reducer";

const IDLE_TIMEOUT_MS = 10 * 60 * 1000;
const WARNING_TIMEOUT_MS = 60 * 1000;
const WARNING_DELAY_MS = IDLE_TIMEOUT_MS - WARNING_TIMEOUT_MS;
const COUNTDOWN_INTERVAL_MS = 250;
const ACTIVITY_THROTTLE_MS = 1000;
const DEFAULT_SECONDS_REMAINING = Math.ceil(WARNING_TIMEOUT_MS / 1000);
const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

function SessionWarningModal({ secondsRemaining, onStayLoggedIn }) {
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 px-4">
      <div className="w-full max-w-md rounded-3xl border-4 border-[#0B2B66] bg-white p-6 text-center shadow-[0_18px_40px_rgba(0,0,0,0.28)]">
        <h2 className="text-2xl font-bold text-[#0B2B66]">
          Session timeout warning
        </h2>
        <p className="mt-3 text-base font-semibold text-gray-700">
          You will be logged out in {secondsRemaining} second
          {secondsRemaining === 1 ? "" : "s"} due to inactivity.
        </p>
        <p className="mt-2 text-sm text-gray-500">
          Select Stay Logged In to continue your session.
        </p>
        <button
          type="button"
          onClick={onStayLoggedIn}
          className="mt-6 rounded-full border-4 border-[#0B2B66] bg-[#FFD54F] px-6 py-3 text-lg font-extrabold text-[#0B2B66] shadow-[0_6px_0_#0B2B66] transition hover:-translate-y-0.5"
        >
          Stay Logged In
        </button>
      </div>
    </div>
  );
}

export default function SessionTimeoutManager() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.user.token);
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(
    DEFAULT_SECONDS_REMAINING
  );

  const warningTimeoutRef = useRef(null);
  const logoutTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const warningDeadlineRef = useRef(0);
  const lastHandledActivityAtRef = useRef(0);

  const clearTimer = (timerRef) => {
    if (!timerRef.current) return;
    window.clearTimeout(timerRef.current);
    window.clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const clearAllTimers = useCallback(() => {
    clearTimer(warningTimeoutRef);
    clearTimer(logoutTimeoutRef);
    clearTimer(countdownIntervalRef);
  }, []);

  const updateCountdown = useCallback(() => {
    const remainingMs = Math.max(warningDeadlineRef.current - Date.now(), 0);
    setSecondsRemaining(Math.ceil(remainingMs / 1000));
  }, []);

  const handleSessionExpired = useCallback(() => {
    clearAllTimers();
    setIsWarningVisible(false);
    setSecondsRemaining(DEFAULT_SECONDS_REMAINING);
    dispatch(sessionExpired());
  }, [clearAllTimers, dispatch]);

  const showWarning = useCallback(() => {
    warningDeadlineRef.current = Date.now() + WARNING_TIMEOUT_MS;
    setIsWarningVisible(true);
    updateCountdown();
    clearTimer(countdownIntervalRef);
    countdownIntervalRef.current = window.setInterval(
      updateCountdown,
      COUNTDOWN_INTERVAL_MS
    );
  }, [updateCountdown]);

  const scheduleInactivityTimers = useCallback(
    (startedAt = Date.now()) => {
      clearAllTimers();
      warningDeadlineRef.current = startedAt + IDLE_TIMEOUT_MS;
      warningTimeoutRef.current = window.setTimeout(showWarning, WARNING_DELAY_MS);
      logoutTimeoutRef.current = window.setTimeout(
        handleSessionExpired,
        IDLE_TIMEOUT_MS
      );
    },
    [clearAllTimers, handleSessionExpired, showWarning]
  );

  const registerActivity = useCallback(
    (force = false) => {
      const now = Date.now();
      if (!force && now - lastHandledActivityAtRef.current < ACTIVITY_THROTTLE_MS) {
        return;
      }

      lastHandledActivityAtRef.current = now;
      setIsWarningVisible(false);
      setSecondsRemaining(DEFAULT_SECONDS_REMAINING);
      scheduleInactivityTimers(now);
    },
    [scheduleInactivityTimers]
  );

  useEffect(() => {
    if (!token) {
      clearAllTimers();
      setIsWarningVisible(false);
      setSecondsRemaining(DEFAULT_SECONDS_REMAINING);
      return undefined;
    }

    registerActivity(true);
    const handleActivity = () => registerActivity(false);

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      clearAllTimers();
    };
  }, [clearAllTimers, registerActivity, token]);

  if (!token || !isWarningVisible) return null;

  return (
    <SessionWarningModal
      secondsRemaining={secondsRemaining}
      onStayLoggedIn={() => registerActivity(true)}
    />
  );
}
